// Modo cadeira: quando a cliente senta, o celular da profissional mostra só a cadeira dela.
// Primeiro nome, serviço, um anel contado do início real (sem número e sem vermelho) e a próxima só pela hora.
// Nada de dinheiro, saúde ou outras clientes: a tela pode ficar virada para quem está sentada.
import { useEffect } from 'react';
import { useNav } from '../app/nav.tsx';
import { can, useAgenda, useMe } from '../lib/data.ts';
import { first, hhmm, isoLocal, toMin } from '../lib/format.ts';
import { useClock } from '../screens/Hoje.tsx';

export function CadeiraMode({ id }: { id: string }) {
  const nav = useNav();
  const me = useMe().data!;
  const { date: today, min: now } = useClock();
  const agenda = useAgenda(today);
  const list = agenda.data ?? [];
  const a = list.find((x) => x.id === id);

  // tela acesa enquanto o modo cadeira está aberto (onde o aparelho deixa)
  useEffect(() => {
    let lock: { release: () => Promise<void> } | null = null;
    const nav2 = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    nav2.wakeLock?.request('screen').then((l) => { lock = l; }).catch(() => undefined);
    return () => { lock?.release().catch(() => undefined); };
  }, []);

  // saiu da cadeira (cobrado, concluído) ou ainda carregando: fecha sozinho quando não há mais o que mostrar
  useEffect(() => { if (agenda.data && (!a || a.status !== 'in_service')) nav.close(); }, [agenda.data, a, nav]);
  if (!a) return <div className="chair" role="dialog" aria-label="Modo cadeira" />;

  const start = a.startedAt ? isoLocal(a.startedAt).min : toMin(a.time);
  const end = isoLocal(a.endsAt);
  const duration = Math.max(1, end.min + (end.date > a.date ? 24 * 60 : 0) - toMin(a.time));
  // o anel só enche até o fim: passou do previsto, ele fica cheio, sem cor de alerta
  const pct = Math.max(0, Math.min(1, (now - start) / duration));
  const next = list.filter((x) => x.professionalId === a.professionalId && x.id !== a.id && ['pending', 'confirmed', 'arrived'].includes(x.status) && toMin(x.time) >= start)
    .sort((x, y) => x.time.localeCompare(y.time))[0];
  const r = 108, c = 2 * Math.PI * r;

  return (
    <div className="chair" role="dialog" aria-modal="true" aria-label={`Modo cadeira: ${first(a.client)}`}>
      <div className="chair-top">
        <b className="chair-who">{first(a.client)}</b>
        <span className="chair-svc">{a.service}</span>
      </div>
      <div className="chair-ring" aria-hidden="true">
        <svg viewBox={`0 0 ${2 * r + 10} ${2 * r + 10}`}>
          <circle className="track" cx={r + 5} cy={r + 5} r={r} />
          <circle className="fill" cx={r + 5} cy={r + 5} r={r} strokeDasharray={c} strokeDashoffset={c * (1 - pct)} />
        </svg>
        <i className="ill cadeira" />
      </div>
      <div className="chair-bottom">
        <p className="chair-next">{next ? <>Depois: <b>{next.time.replace(':00', 'h')}</b></> : 'Sem mais ninguém depois'}</p>
        <div className="chair-acts">
          <button className="cta ghost" onClick={nav.close}>Sair do modo cadeira</button>
          {can(me, 'cobrar') && <button className="cta" onClick={() => nav.open({ kind: 'comanda', id: a.id })}>Terminei · receber</button>}
        </div>
        <p className="chair-hint">Começou às {hhmm(start)}</p>
      </div>
    </div>
  );
}
